import React, { SyntheticEvent, ReactElement, useMemo } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Icon from '@mui/material/Icon';
import Link from '@mui/material/Link';

type DirectoryItem = {
  title: string;
  icon?: ReactElement;
  activePage?: boolean;
};

export type HeaderWithTabsProps = {
  /**
   * @description Page entries to be shown in breadcrumbs. Each entry in the array takes a title string, and optional MUI icon, and active page indicator boolean.
   * @example
   * currentDirectory: [
   *    { title: 'Accounts', icon: <HomeIcon />},
   *    { title: 'Hotpoint', icon: <AccountBoxIcon />, activePage: true },
   * ]
   */
  currentDirectory?: DirectoryItem[];

  /**
   * @description Tab titles to generate tabs. Takes an array of strings.
   * @example
   * tabItems: ['Overview', 'Notifications', 'Admins', 'Account Configurations']
   *
   */
  tabItems: string[];

  /**
   * @description Boolean to indicate whether online or offline. True state will color the circle green, else it will color the circle red/yellow.
   */
  online?: boolean;

  /**
   * @description Listener for events of tab changes
   * @param {SyntheticEvent} event
   * @param {number} newTabIndex
   */
  onTabChange?: (event: SyntheticEvent, newTabIndex: number) => void;

  /**
   * @description Listener for events of breadcrumb item clicks
   * @param {number} clickedCrumbIndex
   */
  onCrumbClick?: (clickedCrumbIndex: number) => void;
  /**
   * TODO: add description
   * Current tab / selected tab
   */
  currentTab?: number;
  /**
   * Element to be displayed at the end of the bar
   */
  headerEndItem?: React.ReactNode;
};

export function HeaderWithTabs({
  currentDirectory = [] as DirectoryItem[],
  tabItems,
  onTabChange = (event, newTabIndex) => {
    console.log('onTabChange', newTabIndex);
  },
  onCrumbClick = (clickedCrumbIndex) => {
    console.log('onCrumbClick', clickedCrumbIndex);
  },
  currentTab,
  headerEndItem,
}: HeaderWithTabsProps) {
  const handleTabChange = (event: SyntheticEvent, newTabIndex: number) => {
    onTabChange(event, newTabIndex);
  };

  const handleCrumbClick = (
    event: SyntheticEvent,
    clickedCrumbIndex: number
  ) => {
    onCrumbClick(clickedCrumbIndex);
  };

  const generateBreadcrumbs = (currentDirectory: DirectoryItem[]) => {
    return currentDirectory.map(
      (directory: DirectoryItem, crumbIndex: number) => (
        <Link
          underline="hover"
          sx={{ display: 'flex' }}
          color={'inherit'}
          key={directory.title}
          onClick={(event) => handleCrumbClick(event, crumbIndex)}
        >
          {directory.icon != undefined ? (
            <Icon sx={{ display: 'flex', mr: 0.5 }}> {directory.icon} </Icon>
          ) : null}
          {directory.title}
        </Link>
      )
    );
  };

  const generateTabs = (tabItems: string[]) => {
    return tabItems.map((item: string, tabValue: number) => (
      <Tab
        key={item}
        value={tabValue}
        label={item}
        sx={{ px: 'auto', fontWeight: 'bold' }}
        onClick={(event) => handleTabChange(event, tabValue)}
      />
    ));
  };

  const listTabs = useMemo(() => generateTabs(tabItems), [tabItems]);
  const listBreadcrumbs = useMemo(
    () => generateBreadcrumbs(currentDirectory),
    [currentDirectory]
  );

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          borderRadius: 1,
          bgcolor: '#0E2F6C',
          height: '48px',
        }}
      >
        {currentDirectory.length > 0 && (
          <Breadcrumbs
            separator="/"
            sx={{
              m: 1,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              color: 'white',
            }}
          >
            {listBreadcrumbs}
          </Breadcrumbs>
        )}
        <div style={{ flex: 1 }} />
        {headerEndItem ? headerEndItem : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          ml: 1,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        {tabItems.length > 0 && <Tabs value={currentTab}>{listTabs}</Tabs>}
      </Box>
    </div>
  );
}
